import React, { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "../ui/card";
import VehicleDetails from "../VehicleDetails";
import LicensePlateStep from "./LicensePlateStep";
import RatingQuestionsStep from "./RatingQuestionsStep";
import SummaryStep from "./SummaryStep";
import AskUserStep from "./AskUserStep";
import { ReviewSubmitFailure, submitReview } from "./submitReview";
import { ReviewFormData, ReviewFormStep } from "./types";
import { LicensePlate, Review } from "../../types";
import { trackEvent } from "../../utils/analytics";
import { trackMetaEvent } from "../../utils/metaPixel";

type StepConfig = {
  slug: ReviewFormStep;
  component: React.FC<any>;
  showVehicleInfo?: boolean;
};

const steps: StepConfig[] = [
  { slug: "license-plate", component: LicensePlateStep },
  { slug: "questions", component: RatingQuestionsStep, showVehicleInfo: true },
  { slug: "summary", component: SummaryStep, showVehicleInfo: true },
  { slug: "ask-user", component: AskUserStep },
];

/**
 * Formulaire d'avis autonome (plaque → notes → récapitulatif) soumis en JSON,
 * réutilisable sur n'importe quelle page.
 * Prérequis de la page hôte : un QueryClientProvider et le meta csrf-token du layout.
 */
export type ReviewsFormProps = {
  review?: ReviewFormData;
  /** Ajoute une étape de collecte d'email avant l'envoi. */
  askUser?: boolean;
  initialStep?: ReviewFormStep;
  errors?: string[];
  /** Endpoint JSON qui reçoit { review: {...} }. */
  submitUrl?: string;
  showVehicleRecap?: boolean;
  className?: string;
  /** Personnalisation des textes de l'étape plaque d'immatriculation. */
  licensePlate?: {
    title?: string;
    tagline?: React.ReactNode | null;
    buttonLabel?: React.ReactNode;
  };
  /** Appelé après un envoi réussi : à la page hôte de décider de la suite. */
  onSuccess: (review: Review) => void;
  onError?: (errors: string[]) => void;
  onStepChange?: (step: ReviewFormStep) => void;
};

const ReviewsForm: React.FC<ReviewsFormProps> = (props) => {
  const {
    askUser = false,
    initialStep = "license-plate",
    submitUrl = "/reviews",
    showVehicleRecap = true,
    className = "",
    onSuccess,
    onError,
    onStepChange,
  } = props;

  const [review, setReview] = useState<ReviewFormData>(props.review || {});
  const [vehicleInfo, setVehicleInfo] = useState<LicensePlate | null>(null);
  const [step, setStep] = useState<ReviewFormStep>(initialStep);
  const [errors, setErrors] = useState<string[]>(props.errors || []);

  const currentStep = steps.find((s) => s.slug === step)!;
  const CurrentStepComponent = currentStep.component;

  useEffect(() => {
    const target = document.getElementById("vehicle-details-recap");
    if (target) {
      const y = target.getBoundingClientRect().top + window.scrollY - 10;
      window.scrollTo({ top: y, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const goToStep = (next: ReviewFormStep) => {
    setStep(next);
    onStepChange?.(next);
  };

  const { mutate, isPending } = useMutation<
    Review,
    ReviewSubmitFailure,
    ReviewFormData
  >({
    mutationFn: (data) => submitReview(data, submitUrl),
    onSuccess: (createdReview) => {
      setErrors([]);
      onSuccess(createdReview);
    },
    onError: (failure) => {
      setErrors(failure.errors);
      // Le token permet d'afficher le bloc de parrainage sur une plaque déjà évaluée.
      if (failure.review?.token) {
        setReview((old) => ({ ...old, token: failure.review!.token }));
      }
      goToStep("summary");
      onError?.(failure.errors);
    },
  });

  const onCreate = (data: ReviewFormData) => {
    trackEvent({
      name: "review_submitted",
      params: {
        category: "Interaction",
        action: "Click",
        label: "review_submitted",
        page_type: "review form",
      },
    });
    trackMetaEvent(
      "Purchase",
      {
        value: 0,
        currency: "EUR",
        content_ids: ["review"],
        content_type: "product",
        event_name: "review_submitted",
      },
      {
        eventID: "review_submitted",
      },
    );
    mutate(data);
  };

  const onStepSubmit = (data: any, other?: { vehicleInfo?: LicensePlate }) => {
    if (step === "license-plate") {
      setReview((old) => ({ ...old, license_plate: data.license_plate }));
      if (other?.vehicleInfo) {
        setVehicleInfo(other.vehicleInfo);
      }
      goToStep("questions");
      return;
    }

    if (step === "questions") {
      setReview({ ...review, ...data });
      goToStep("summary");
      return;
    }

    if (step === "summary") {
      if (askUser) {
        goToStep("ask-user");
      } else {
        onCreate(review);
      }
      return;
    }

    if (step === "ask-user") {
      const nextReview = { ...review, ...data };
      setReview(nextReview);
      onCreate(nextReview);
    }
  };

  const onPreviousStep = () => {
    if (step === "summary") {
      goToStep("questions");
    }
  };

  return (
    <div className={`flex flex-col gap-8 ${className}`}>
      {showVehicleRecap && currentStep.showVehicleInfo && vehicleInfo && (
        <Card className="w-full max-w-2xl gap-4" id="vehicle-details-recap">
          <CardContent>
            <VehicleDetails vehicleInfo={vehicleInfo} />
          </CardContent>
        </Card>
      )}
      <Card className="w-full max-w-2xl gap-4">
        <CardContent>
          <CurrentStepComponent
            review={review}
            errors={errors}
            isSubmitting={isPending}
            onSubmit={onStepSubmit}
            onPrevious={onPreviousStep}
            {...(step === "license-plate" ? props.licensePlate : undefined)}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewsForm;
